// client/src/pages/MyProducts.js
import { useState, useEffect, useContext, useCallback } from 'react';
import React from 'react';
import { AuthContext } from '../context/AuthContext';
import ProductForm from '../components/ProductForm';
import BulkImportDialog from '../components/BulkImportDialog';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, IconButton, Stack, Alert, CircularProgress, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';
import { useStore } from '../context/StoreContext';

const MyProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);
  const { store } = useStore();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/seller/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch products.');
      setProducts(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchProducts(); }, [token, fetchProducts]);

  const handleSaveProduct = async (formData) => {
    const isEditing = !!editingProduct;
    const url = isEditing
      ? `${API_BASE_URL}/api/seller/products/${editingProduct.id}`
      : `${API_BASE_URL}/api/seller/products`;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save product.');
      }
      fetchProducts();
      setIsFormVisible(false);
      setEditingProduct(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to export products.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to permanently delete this product?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/seller/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete product.');
      fetchProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>My Products</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport}>Export CSV</Button>
          <Button size="small" startIcon={<FileUploadOutlinedIcon />} onClick={() => setImportOpen(true)}>
            Import products
          </Button>
          {!isFormVisible && (
            <Button variant="contained" onClick={() => { setEditingProduct(null); setIsFormVisible(true); }}>
              Add New Product
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isFormVisible && (
        <ProductForm
          product={editingProduct}
          onSave={handleSaveProduct}
          onCancel={() => { setIsFormVisible(false); setEditingProduct(null); }}
        />
      )}

      <BulkImportDialog open={importOpen} onClose={() => setImportOpen(false)} token={token} onImported={fetchProducts} />

      <Typography variant="h6" fontWeight={700} gutterBottom>Your Listings</Typography>
      {products.length === 0 && !isFormVisible ? (
        <EmptyState
          icon={<Inventory2OutlinedIcon />}
          title="You haven't listed any products yet"
          description={`Add your first product to start selling on ${store.name}.`}
          actionLabel="Add New Product"
          onAction={() => setIsFormVisible(true)}
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell><TableCell>Name</TableCell><TableCell>Price</TableCell><TableCell>Stock</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{formatCurrency(product.price)}</TableCell>
                  <TableCell>
                    {product.quantity}
                    {Number(product.quantity) > 0 && Number(product.quantity) <= LOW_STOCK_THRESHOLD && (
                      <Chip label="Low Stock" color="warning" size="small" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => { setEditingProduct(product); setIsFormVisible(true); window.scrollTo(0, 0); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeleteProduct(product.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MyProducts;
